import type { AtlasMission } from '../atlas.types';

/**
 * `first-report` — Reports page guided tour (Iteration 7 rewrite).
 *
 * Anchors live in the new Reports surface and use the existing
 * `getElementById`-based scroll mechanism. Each anchor element also carries a
 * `data-atlas-anchor="<short-name>"` attribute so future selector-based
 * highlights (e.g. CSS spotlight on tour open) can target them without
 * hard-coding ids in styles.
 *
 * Permission-gated: only Owner / Administrator (= holders of
 * `reports.access`) ever see this tour. Members never reach `/reports` and
 * the mission is filtered out of the Dock for them.
 */
export const reportsMission: AtlasMission = {
  id: 'first-report',
  title: 'Build your first report',
  description: 'Generate a stakeholder-ready report from your Jira signals in under five minutes.',
  kicker: 'Guided tour',
  estimatedMinutes: 5,
  entryRoute: '/reports',
  featureKey: 'atlas.firstReport',
  storage: 'atlas',
  requiredPermission: 'reports.access',
  steps: [
    {
      id: 'choose-template',
      title: 'Choose a template',
      body:
        'Start with a curated template — Executive Brief, Delivery Review, Release Notes, ' +
        'Analytics — or pick Blank to build from scratch. Each template seeds the report ' +
        'with the sections that matter for that audience.',
      anchorId: 'reports-templates-anchor',
      primaryLabel: 'Next →',
    },
    {
      id: 'configure-scope',
      title: 'Configure the scope',
      body:
        'Pick a release, a date range, or a quick timeframe like "This quarter". ' +
        'Release Notes can combine multiple fixVersions and even cross-product releases.',
      anchorId: 'reports-scope-picker-anchor',
      primaryLabel: 'Next →',
    },
    {
      id: 'arrange-blocks',
      title: 'Add or rearrange blocks',
      body:
        'Drag blocks to reorder, click "+ Add block" to insert new content from the catalog, ' +
        'or type "/" inside a Free text block to convert it. The outline mirrors the report.',
      anchorId: 'reports-block-tree-anchor',
      primaryLabel: 'Next →',
    },
    {
      id: 'preview',
      title: 'Preview your report',
      body:
        'The live preview shows the exact document, slide deck, or outline you will export. ' +
        'Click any block to fine-tune it in the right-rail inspector.',
      anchorId: 'reports-preview-anchor',
      primaryLabel: 'Next →',
    },
    {
      id: 'export',
      title: 'Export and download',
      body:
        'Export to PDF, DOCX, or PPTX (Executive Brief only). Save the current setup as a ' +
        'preset to reuse next time — your changes are also auto-saved as a draft.',
      anchorId: 'reports-toolbar-anchor',
      primaryLabel: 'Done',
    },
  ],
};
